import { useContext, useEffect, useState } from "react";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  useProveedoresApi,
  ProveedorItem,
  ProveedorDto,
} from "../../../services/api/proveedoresApi";
import ProveedorDataTable from "../../../components/module/admin/GenericDataTable/ProveedorDataTable";
import ProveedorModal from "../../../components/module/admin/ProveedorModal/ProveedorModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Proveedores = () => {
  const [items, setItems] = useState<ProveedorItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProveedorItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useProveedoresApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 40 },
    {
      field: "tipoDocumento",
      headerName: "Tipo Doc.",
      flex: 1,
      valueGetter: (params) => params.row.tipoDocumento?.Nombre || "",
    },
    { field: "NumeroIdentificacion", headerName: "Número", flex: 1 },
    { field: "Nombre", headerName: "Nombre", flex: 1 },
    { field: "RepresentanteLegal", headerName: "Representante Legal", flex: 1 },
    { field: "Telefono", headerName: "Teléfono", flex: 1 },
    { field: "Correo", headerName: "Correo", flex: 1 },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    const data = await getAll();
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: ProveedorItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción eliminará el proveedor.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        await remove(id);
        await loadItems();
        toast.success("El proveedor ha sido eliminado.");
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el registro.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} este proveedor?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: ProveedorDto = {
            IdTipoDocumento: item.IdTipoDocumento,
            NumeroIdentificacion: item.NumeroIdentificacion,
            Nombre: item.Nombre,
            RepresentanteLegal: item.RepresentanteLegal,
            Direccion: item.Direccion,
            Telefono: item.Telefono,
            Correo: item.Correo,
            Web: item.Web,
            Activo: active,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado del proveedor ha sido actualizado.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("No se pudo actualizar el estado.");
      }
    }
  };

  const handleModalSubmit = async (data: ProveedorDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      await loadItems();
      toast.success("Registro guardado correctamente");
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("No se pudo guardar el registro.");
    }
  };

  return (
    <PageLayout icon={LocalShippingIcon} title="Proveedores">
        <div style={{ padding: "24px" }}>
          <ProveedorDataTable
            items={items}
            columns={columns}          
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            isLoading={isLoading}
          />
          <ProveedorModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Proveedor" : "Nuevo Proveedor"}
            initialData={
              editingItem
                ? {
                    IdTipoDocumento: editingItem.IdTipoDocumento,
                    NumeroIdentificacion: editingItem.NumeroIdentificacion,
                    Nombre: editingItem.Nombre,
                    RepresentanteLegal: editingItem.RepresentanteLegal,
                    Direccion: editingItem.Direccion,
                    Telefono: editingItem.Telefono,
                    Correo: editingItem.Correo,
                    Web: editingItem.Web,
                    Activo: editingItem.Activo,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Proveedores;